'use client';

import React from 'react';
import Link from 'next/link';
import ClinigolLogo from '@/components/ui/ClinigolLogo';

export default function LandingPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0e0818' }}>
      {/* ── Minimal Nav ── */}
      <nav
        className="relative z-30 flex items-center justify-between px-6 md:px-10 py-5"
        style={{ background: 'transparent' }}
      >
        <ClinigolLogo width={120} height={40} color="rgba(255,255,255,0.92)" />
        <div className="flex items-center gap-2">
          <Link
            href="/sign-up-login"
            className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200"
            style={{
              fontFamily: 'var(--font-geist-sans)',
              color: 'rgba(255,255,255,0.72)',
              border: '1px solid rgba(255,255,255,0.14)',
              background: 'rgba(255,255,255,0.06)',
            }}
          >
            Login
          </Link>
          <Link
            href="/sign-up-login"
            className="px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200"
            style={{
              fontFamily: 'var(--font-geist-sans)',
              color: '#ffffff',
              background: '#681f77',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            Register
          </Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative flex-1 flex flex-col justify-center overflow-hidden" style={{ minHeight: '88vh' }}>
        {/* Background: Weimaraner image with gradient overlay */}
        <div className="absolute inset-0 z-0">
          {/* Photo layer */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(/assets/images/CNS_Broad-1778870716496.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center 30%',
              backgroundRepeat: 'no-repeat',
            }}
          />
          {/* Atmospheric amber-to-purple gradient overlay */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(135deg, rgba(196,122,43,0.72) 0%, rgba(160,82,122,0.68) 28%, rgba(104,31,119,0.82) 55%, rgba(59,16,96,0.92) 75%, rgba(14,8,24,0.97) 100%)',
            }}
          />
          {/* Grain texture */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.07'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'repeat',
              backgroundSize: '200px 200px',
              opacity: 0.6,
              mixBlendMode: 'overlay',
            }}
          />
          {/* Bottom fade to dark */}
          <div
            className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
            style={{ background: 'linear-gradient(to bottom, transparent, #0e0818)' }}
          />
        </div>

        {/* Hero content */}
        <div className="relative z-10 px-6 md:px-10 lg:px-16 max-w-5xl">
          {/* Eyebrow */}
          <p
            className="text-xs font-medium tracking-widest uppercase mb-8"
            style={{
              fontFamily: 'var(--font-geist-sans)',
              color: 'rgba(255,255,255,0.45)',
              letterSpacing: '0.16em',
            }}
          >
            ■ &nbsp;Clinigol Trials Portal
          </p>

          {/* Main headline */}
          <h1
            style={{
              fontFamily: 'var(--font-newsreader)',
              fontSize: 'clamp(2.8rem, 7vw, 6rem)',
              lineHeight: 1.04,
              letterSpacing: '-0.03em',
              color: '#ffffff',
              fontWeight: 400,
              fontStyle: 'italic',
              maxWidth: '820px',
            }}
          >
            Their loyalty,
            <br />
            our war.
          </h1>

          {/* Subline */}
          <p
            className="mt-5 mb-8"
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: 'rgba(255,255,255,0.72)',
              fontWeight: 300,
              letterSpacing: '0.01em',
              maxWidth: '560px',
              lineHeight: 1.4,
            }}
          >
            Engineering the Biology of Survival.
          </p>

          {/* Mission copy */}
          <p
            className="mb-12"
            style={{
              fontFamily: 'var(--font-geist-sans)',
              fontSize: 'clamp(0.875rem, 1.4vw, 1rem)',
              color: 'rgba(255,255,255,0.52)',
              fontWeight: 400,
              maxWidth: '480px',
              lineHeight: 1.7,
            }}
          >
            We are building the next frontier, developing nutritional tools that solve the toughest challenges dogs and cats face today.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              href="/current-trials-page"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:opacity-90"
              style={{
                fontFamily: 'var(--font-geist-sans)',
                background: 'linear-gradient(135deg, #c47a2b 0%, #a0527a 60%, #681f77 100%)',
                color: '#ffffff',
                border: '1px solid rgba(255,255,255,0.12)',
                letterSpacing: '0.01em',
              }}
            >
              Join a Trial
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.5 7H11.5M11.5 7L8 3.5M11.5 7L8 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link
              href="/sign-up-login"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold transition-all duration-200"
              style={{
                fontFamily: 'var(--font-geist-sans)',
                color: 'rgba(255,255,255,0.85)',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.18)',
                backdropFilter: 'blur(12px)',
                letterSpacing: '0.01em',
              }}
            >
              For Vets
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  background: 'rgba(255,255,255,0.14)',
                  color: 'rgba(255,255,255,0.6)',
                  fontFamily: 'var(--font-geist-sans)',
                  fontSize: '0.65rem',
                  letterSpacing: '0.06em',
                }}
              >
                Register · CPD
              </span>
            </Link>
          </div>
        </div>

        {/* Bottom-right atmospheric accent */}
        <div
          className="absolute bottom-0 right-0 w-96 h-96 pointer-events-none"
          style={{
            background: 'radial-gradient(circle at 80% 80%, rgba(196,122,43,0.12) 0%, transparent 70%)',
          }}
        />
      </section>

      {/* ── Footer ── */}
      <footer
        className="relative z-10 flex items-center justify-center px-6 py-5"
        style={{
          background: '#0e0818',
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <a
          href="https://clinigol.vet"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs transition-opacity duration-200 hover:opacity-80"
          style={{
            fontFamily: 'var(--font-geist-sans)',
            color: 'rgba(255,255,255,0.28)',
            letterSpacing: '0.04em',
          }}
        >
          clinigol.vet
        </a>
      </footer>
    </div>
  );
}